/**
 * Saved filters — the filters the Windows filter builder stores in
 * `stbl_Database_Filters` and shares between users through
 * `stbl_Database_FiltersPermissions`.
 *
 * This module is the pure part: the shape of a saved filter, how the raw
 * database records map onto it, and who is allowed to do what with one. The
 * loading and the mutations live in `useSavedFilters` in
 * `@olenbetong/appframe-react`.
 */

import type { FilterAccessLevel } from "./types.js";

/**
 * Access a user has to a saved filter.
 *
 * `sviw_Database_MyFilters` only ever reports `Reader`, `Editor` or `Manager`,
 * and a filter the current user created is always reported as `Manager`.
 */
export type { FilterAccessLevel };

/**
 * `sviw_Database_MyFilters` unions in a synthetic "No Filter" row so the Windows
 * tree can render a "no filter" entry. It is not a real filter and must be
 * dropped before the list is shown.
 */
export const NO_FILTER_ID = -1;

/** A saved filter, as used by the filter builder UI. */
export type SavedFilter = {
	filterId: number;
	primKey: string;
	name: string;
	/** DevExpress criteria string, e.g. `[Name] Like '%a%'`. */
	criteria: string;
	dbObjectId: string;
	accessLevel: FilterAccessLevel;
	/** True when the current user created the filter. */
	createdByUser: boolean;
	createdBy: string;
	domain: string | null;
	/** True when the filter is visible in every domain the user has access to. */
	crossDomain: boolean;
	/** True when the filter is shared with individual users rather than groups. */
	isPersonallyShared: boolean;
	/** True when the criteria are hidden from readers of a shared filter. */
	hideCriteria: boolean;
	headerText: string | null;
	comment: string | null;
	/** Report parameter values, stored as XML. Only used by report filters. */
	parameters: string | null;
	reportTitle: string | null;
	created: Date | null;
	updated: Date | null;
};

/** A raw `sviw_Database_MyFilters` / `sviw_Database_FiltersFromReportID` record. */
export type SavedFilterRecord = {
	PrimKey?: string | null;
	FilterID?: number | null;
	DBObjectID?: string | null;
	FilterName?: string | null;
	FilterCriteria?: string | null;
	AccessLevel?: string | null;
	CreatedByUser?: string | boolean | null;
	CreatedBy?: string | null;
	Domain?: string | null;
	CrossDomain?: boolean | number | null;
	IsPersonallyShared?: boolean | number | null;
	HideFilterCriteria?: boolean | number | null;
	OptionalHeaderText?: string | null;
	OptionalComment?: string | null;
	Parameters?: string | null;
	ReportTitle?: string | null;
	Created?: Date | string | null;
	Updated?: Date | string | null;
};

/**
 * Maps a database record onto a `SavedFilter`.
 *
 * `CreatedByUser` needs care: the view computes it as the *string* `'true'` or
 * `'false'` (and `'False'` on the synthetic row), not as a bit.
 */
export function toSavedFilter(record: SavedFilterRecord): SavedFilter {
	return {
		filterId: record.FilterID ?? NO_FILTER_ID,
		primKey: record.PrimKey ?? "",
		name: record.FilterName ?? "",
		criteria: record.FilterCriteria ?? "",
		dbObjectId: record.DBObjectID ?? "",
		accessLevel: toAccessLevel(record.AccessLevel),
		createdByUser: toBoolean(record.CreatedByUser),
		createdBy: record.CreatedBy ?? "",
		domain: record.Domain ?? null,
		crossDomain: toBoolean(record.CrossDomain),
		isPersonallyShared: toBoolean(record.IsPersonallyShared),
		hideCriteria: toBoolean(record.HideFilterCriteria),
		headerText: record.OptionalHeaderText || null,
		comment: record.OptionalComment || null,
		parameters: record.Parameters || null,
		reportTitle: record.ReportTitle || null,
		created: toDate(record.Created),
		updated: toDate(record.Updated),
	};
}

function toAccessLevel(value: string | null | undefined): FilterAccessLevel {
	if (value === "Manager" || value === "Editor") return value;

	return "Reader";
}

function toBoolean(value: string | boolean | number | null | undefined): boolean {
	if (typeof value === "string") return value.toLowerCase() === "true";

	return Boolean(value);
}

function toDate(value: Date | string | null | undefined): Date | null {
	if (!value) return null;
	if (value instanceof Date) return value;

	let date = new Date(value);

	return Number.isNaN(date.getTime()) ? null : date;
}

/** Drops the synthetic "No Filter" row and sorts by name. */
export function toSavedFilters(records: SavedFilterRecord[]): SavedFilter[] {
	return records
		.filter((record) => (record.FilterID ?? NO_FILTER_ID) !== NO_FILTER_ID)
		.map(toSavedFilter)
		.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Splits filters into the two branches the Windows tree shows: the ones the user
 * created, and the ones that have been shared with them.
 */
export function partitionSavedFilters(filters: SavedFilter[]): { mine: SavedFilter[]; shared: SavedFilter[] } {
	let mine: SavedFilter[] = [];
	let shared: SavedFilter[] = [];

	for (let filter of filters) {
		if (filter.createdByUser) {
			mine.push(filter);
		} else {
			shared.push(filter);
		}
	}

	return { mine, shared };
}

export function findSavedFilter(filters: SavedFilter[], filterId: number | null | undefined) {
	if (filterId == null) return undefined;

	return filters.find((filter) => filter.filterId === filterId);
}

/** True when the user may overwrite the filter's criteria. */
export function canEditSavedFilter(filter: SavedFilter): boolean {
	return filter.accessLevel === "Editor" || filter.accessLevel === "Manager";
}

/** True when the user may change who the filter is shared with. */
export function canShareSavedFilter(filter: SavedFilter): boolean {
	return filter.accessLevel === "Manager";
}

/**
 * True when the user may rename or delete the filter.
 *
 * `sstp_WebSiteCMS_FilterBuilder_RenameFilter` and `…_DeleteFilter` both require
 * `CreatedBy = SUSER_SNAME()` — the manager-access branch is commented out in
 * both procedures, deliberately, because the UI only ever offers these for a
 * user's own filters.
 */
export function canRenameSavedFilter(filter: SavedFilter): boolean {
	return filter.createdByUser;
}

export const canDeleteSavedFilter = canRenameSavedFilter;

/**
 * True when the user may toggle the cross-domain flag.
 *
 * `sstp_Database_Filters_SetCrossDomain` also filters on
 * `CreatedBy = SUSER_SNAME()`.
 */
export const canSetCrossDomain = canRenameSavedFilter;
